import { FormEvent, useEffect, useMemo, useState } from 'react';
import { PlanningSprint, PlanningSprintUpsertPayload } from '../../services/strategicPlanningApi';
import { COMPANY_AREAS, CompanyAreaId } from '../../types/CompanyArea';
import { addDaysIso, todayIsoDate } from '../../types/PlanningSprint';
import { DraggableModalPanel } from '../ui/DraggableModalPanel';
import { RequiredMark } from '../ui/RequiredMark';

const DEFAULT_SPRINT_LENGTH_DAYS = 15;

type PlanningSprintFormModalProps = {
  open: boolean;
  initial?: PlanningSprint | null;
  defaultArea: CompanyAreaId;
  existingSprints: PlanningSprint[];
  onClose: () => void;
  onSubmit: (payload: PlanningSprintUpsertPayload) => Promise<void>;
};

export function PlanningSprintFormModal({
  open,
  initial,
  defaultArea,
  existingSprints,
  onClose,
  onSubmit,
}: PlanningSprintFormModalProps) {
  const [title, setTitle] = useState('');
  const [startDate, setStartDate] = useState(todayIsoDate());
  const [endDate, setEndDate] = useState(addDaysIso(todayIsoDate(), DEFAULT_SPRINT_LENGTH_DAYS));
  const [area, setArea] = useState<CompanyAreaId>(defaultArea);
  const [saving, setSaving] = useState(false);

  const suggestedTitle = useMemo(() => {
    const count = existingSprints.filter((sprint) => sprint.area === defaultArea).length;
    return `Sprint ${count + 1}`;
  }, [defaultArea, existingSprints]);

  useEffect(() => {
    if (!open) return;

    if (initial) {
      setTitle(initial.title);
      setStartDate(initial.startDate);
      setEndDate(initial.endDate);
      setArea(initial.area as CompanyAreaId);
      return;
    }

    const areaSprints = [...existingSprints]
      .filter((sprint) => sprint.area === defaultArea)
      .sort((left, right) => left.endDate.localeCompare(right.endDate));
    const lastSprint = areaSprints[areaSprints.length - 1];
    const nextStart = lastSprint ? addDaysIso(lastSprint.endDate, 1) : todayIsoDate();

    setTitle(suggestedTitle);
    setStartDate(nextStart);
    setEndDate(addDaysIso(nextStart, DEFAULT_SPRINT_LENGTH_DAYS));
    setArea(defaultArea);
  }, [open, initial, defaultArea, existingSprints, suggestedTitle]);

  function applyStartDate(nextStartDate: string): void {
    setStartDate(nextStartDate);
    if (!initial && nextStartDate) {
      setEndDate(addDaysIso(nextStartDate, DEFAULT_SPRINT_LENGTH_DAYS));
    }
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setSaving(true);
    try {
      await onSubmit({
        title: title.trim(),
        startDate,
        endDate,
        area,
      });
      onClose();
    } finally {
      setSaving(false);
    }
  }

  return (
    <DraggableModalPanel
      open={open}
      title={initial ? 'Editar sprint' : 'Nova sprint'}
      overlayClassName="z-[70]"
      onClose={onClose}
      footer={
        <div className="erp-btn-actions">
          <button type="button" className="erp-btn-secondary" onClick={onClose} disabled={saving}>
            Cancelar
          </button>
          <button
            type="submit"
            form="planning-sprint-form"
            className="erp-btn-primary"
            disabled={saving || !title.trim()}
          >
            {saving ? 'Salvando…' : initial ? 'Salvar' : 'Criar'}
          </button>
        </div>
      }
    >
      <form id="planning-sprint-form" className="space-y-4" onSubmit={(event) => void handleSubmit(event)}>
        <label className="block">
          <span className="erp-label">
            Título <RequiredMark />
          </span>
          <input
            className="erp-input"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            required
            maxLength={200}
            autoFocus
          />
        </label>

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="erp-label">
              Início <RequiredMark />
            </span>
            <input
              type="date"
              className="erp-input"
              value={startDate}
              onChange={(event) => applyStartDate(event.target.value)}
              required
            />
          </label>
          <label className="block">
            <span className="erp-label">
              Fim <RequiredMark />
            </span>
            <input
              type="date"
              className="erp-input"
              value={endDate}
              onChange={(event) => setEndDate(event.target.value)}
              min={startDate}
              required
            />
          </label>
        </div>

        <label className="block">
          <span className="erp-label">
            Área <RequiredMark />
          </span>
          <select
            className="erp-input"
            value={area}
            onChange={(event) => setArea(event.target.value as CompanyAreaId)}
          >
            {COMPANY_AREAS.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      </form>
    </DraggableModalPanel>
  );
}
